import React from 'react'
import { useForm, ValidateInput, DropdownCountry, DropdownState } from 'usbc-web-react-library'
import { formatZipCode } from '../../shared/helpers';

const requiredFieldMsg = (fieldName) => `Please enter ${fieldName}`;
const requiredSelectMsg = (fieldName) => `Please select ${fieldName}`;
const invalidFieldMsg = (fieldName) => `${fieldName} is invalid`;

function OrderForm(props) {

    const {
        handleChange,
        handleSubmit,
        setValues,
        updateError,
        values,
        errors
    } = useForm(saveOrderCallback, validate);

    React.useEffect(() => {
        setValues(props.order || {});
    }, [props.order]);

    function formatPhoneNumber(value, country) {
        if (!value || (country != "US" && country != "CA")) return value;
        const phonenumber = value.replace(/[^\d]/g, '');
        const phonelength = phonenumber.length;
        if (phonelength < 4) return phonenumber;
        if (phonelength <= 6) return `(${phonenumber.slice(0, 3)}) ${phonenumber.slice(3)}`;
        if (phonelength > 6 && phonelength < 10) return `(${phonenumber.slice(0, 3)}) ${phonenumber.slice(3, 6)}-${phonenumber.slice(6)}`;
        if (phonelength > 10) return `(${phonenumber.slice(0, 3)}) ${phonenumber.slice(3, 6)}-${phonenumber.slice(6, 10)} x${phonenumber.slice(10)}`;

        return `(${phonenumber.slice(0, 3)}) ${phonenumber.slice(3, 6)}-${phonenumber.slice(6)}`;
    }

    function saveOrderCallback() {
        props.onValidOrderSubmitted(values);
    }

    function validate(name, value, displayName) {
        let testValue = value.trim();
        switch (name) {
            case 'emailAddress':
                value = testValue;
                if (value == null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else if (
                    !new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(value)
                ) {
                    updateError(name, 'Please enter a valid email address');
                }
                else {
                    updateError(name);
                }
                break;
            case 'phoneNumber':
                if (testValue == null || testValue === "") {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else {
                    testValue = formatPhoneNumber(testValue, values.country)?.replace(/( |-)+$/g, '');
                    if ((values.country == "US" || values.country == "CA") && !new RegExp(/(\d{3})\)s* \d{3}-\d{4}( X\d{0,})?/gm).test(testValue)) {
                        updateError(name, 'Please enter a valid phone number in (999) 999 9999 x9999 format');
                    }
                    else {
                        updateError(name);
                    }
                    value = testValue;
                }
                break;
            case 'address1':
                if (value == null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else if (
                    new RegExp(/p[.| |. ]{0,2}o[.| |. ]{0,2}b/gmi).test(value)
                ) {
                    updateError(name, 'We cannot ship to a P.O. box');
                }
                else {
                    updateError(name);
                }
                break;
            case 'city':
                if (value == null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                } else {
                    updateError(name);
                }
                break;
            case 'state':
                if (value == null || value === "") {
                    updateError(name, requiredSelectMsg(displayName));
                } else {
                    updateError(name);
                }
                break;

            case 'attention':
                if (value == null || value === "" ||testValue=="") {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else {
                    updateError(name);
                }
                break;

            case 'zip':
                if ((values.country === "US" || values.country === "CA") && (testValue === null || testValue === "")) {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else {
                    testValue = formatZipCode(testValue, values.country)?.replace(/( |-)+$/g, '');
                    if (values.country === "US") {
                        if (!new RegExp(/^\d{5}$|(\d{5}\-\d{4})/gm).test(testValue)) {
                            updateError(name, 'Please enter a valid zip code');
                        }
                        else {
                            updateError(name);
                        }
                    }
                    else if (values.country === "CA") {
                        if (!new RegExp(/^[A-Za-z]\d[A-Za-z]-\d[A-Za-z]\d$/gm).test(testValue)) {
                            updateError(name, 'Please enter a valid zip code');
                        }
                        else {
                            updateError(name);
                        }
                    }
                    else {
                        updateError(name);
                    }
                    value = testValue;
                }
                break;
            case 'country':
                if (value === null || value === "") {
                    updateError(name, requiredSelectMsg(displayName));
                }
                else if (value !== "CA" && values.zip && !/[0-9]/.test(values.zip)) {
                    updateError("zip", invalidFieldMsg("Zip/Postal"));
                }
                else {
                    updateError(name);
                }
                break;
            default:
                break;
        }
        return value;
    }

    return (
        values != null &&
        <form id="orderInfo-form" className="container" onSubmit={handleSubmit} noValidate>
            <div className='row'>
                <div className="col-md-4">
                    <label htmlFor="name" className="form-label">Order Number</label>
                    <input type="text" className="form-control" id="name" name='name'
                        value={values?.name || ''} disabled />
                </div>
                <div className="col-md-4">
                    <label htmlFor="attention" className={`form-label ${props.readOnly ? "" : "required"}`}>Attention</label>
                    <ValidateInput errors={errors}>
                        <input type="text" className="form-control" id="attention" name='attention'
                            value={values?.attention || ''} onChange={handleChange} maxLength={30}
                            disabled={props.readOnly} />
                    </ValidateInput>
                </div>
            </div>
            <div className='row mt-3'>
                <div className="col-md-4">
                    <label htmlFor="emailAddress" className={`form-label ${props.readOnly ? "" : "required"}`}>Email</label>
                    <ValidateInput errors={errors}>
                        <input type="email" className="form-control" id="emailAddress" name='emailAddress'
                            value={values?.emailAddress || ''} onChange={handleChange} maxLength={50}
                            disabled={props.readOnly} />
                    </ValidateInput>
                </div>
                <div className="col-md-4">
                    <label htmlFor="phoneNumber" className={`form-label ${props.readOnly ? "" : "required"}`}>Phone</label>
                    <ValidateInput errors={errors}>
                        <input type="text" className="form-control" id="phoneNumber" name='phoneNumber'
                            autoComplete='false' placeholder="(999)999-9999 x9999"
                            value={values?.phoneNumber || ''} onChange={handleChange} maxLength={19}
                            disabled={props.readOnly} />
                    </ValidateInput>
                </div>
            </div>
            <div className='row mt-3'>
                <div className="col-md-4">
                    <label htmlFor="address1" className={`form-label ${props.readOnly ? "" : "required"}`}>Address 1</label>
                    <ValidateInput errors={errors}>
                        <input type="text" className="form-control" id="address1" name='address1'
                            value={values?.address1 || ''} onChange={handleChange} maxLength={30}
                            disabled={props.readOnly} />
                    </ValidateInput>
                </div>
                <div className="col-md-4">
                    <label htmlFor="address2" className="form-label">Address 2</label>
                    <input type="text" className="form-control" id="address2" name='address2'
                        value={values?.address2 || ''} onChange={handleChange} maxLength={30}
                        disabled={props.readOnly} />
                </div>
                <div className={"col-md-4"}>
                    <DropdownCountry className="form-control form-select col-6 countryFieldDropdown"
                        id="countryField"
                        name='country'
                        required={!props.readOnly}
                        value={values?.country || ''}
                        onChange={handleChange}
                        errors={errors}
                        disabled={props.readOnly}
                        readOnly={props.readOnly} />
                </div>
            </div>
            <div className='row mt-3'>
                <div className="col-md-4">
                    <label htmlFor="city" className={`form-label ${props.readOnly ? "" : "required"}`}>City</label>
                    <ValidateInput errors={errors}>
                        <input type="text" className="form-control" id="city" name='city'
                            value={values?.city || ''} onChange={handleChange} maxLength={20}
                            disabled={props.readOnly} />
                    </ValidateInput>
                </div>
                <div className={"col-md-4"}>
                    <DropdownState className="form-control form-select col-6 stateFieldDropdown"
                        id="stateField"
                        name='state'
                        required={!props.readOnly}
                        value={values?.state || ''}
                        onChange={handleChange}
                        errors={errors}
                        selectedCountry={values?.country || ''}
                        disabled={props.readOnly}
                        readOnly={props.readOnly} />
                </div>
                <div className="col-md-4">
                    <label htmlFor="zip" className={`form-label ${props.readOnly ? "" : "required"}`}>Zip Code</label>
                    <ValidateInput errors={errors}>
                        <input type="text" className="form-control" id="zip" name='zip'
                            value={values.zip} onChange={handleChange} maxLength={10}
                            disabled={props.readOnly}
                            pattern={values.country == "CA" ? "[a-zA-Z0-9]{5}" : "[0-9]{5}"}
                            onKeyPress={(e) => (
                                values.country == "CA" && !/[a-zA-Z0-9]/.test(e.key) && e.preventDefault()) ||
                                (values.country != "CA" && !/[0-9]/.test(e.key) && e.preventDefault())}
                        />
                    </ValidateInput>
                </div>
            </div>
        </form>
    )
};

export default OrderForm;

